import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { OnboardingShell } from '../OnboardingShell';

export const TermsStep = (props) => {
	const { stepIndex, totalSteps, onBack, onNext } = props;
	const { getAccessTokenSilently } = useAuth0();

	const [hasScrolled, setHasScrolled] = useState(false);
	const [agreed, setAgreed] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const canContinue = hasScrolled && agreed && !submitting;

	const handleScroll = (e) => {
		const { scrollTop, scrollHeight, clientHeight } = e.target;
		if (scrollTop + clientHeight >= scrollHeight - 40) {
			setHasScrolled(true);
		}
	};

	const handleContinue = async () => {
		if (!canContinue) return;
		setSubmitting(true);
		try {
			const token = await getAccessTokenSilently();
			const userdb = JSON.parse(window.localStorage.getItem('userdb'));
			await axios.get(`${process.env.REACT_APP_BACKEND_URL}/terms_completed?terms_completed=true&email=${userdb.email}`, {
				headers: { Authorization: `Bearer ${token}` },
			});
		} catch (e) {
			console.error('terms_completed failed', e);
		} finally {
			setSubmitting(false);
			onNext();
		}
	};

	return (
		<OnboardingShell
			stepIndex={stepIndex}
			totalSteps={totalSteps}
			title="Terms & Conditions"
			subtitle="Please review and accept to continue."
			showBack={!!onBack}
			onBack={onBack}
			canContinue={canContinue}
			onContinue={handleContinue}
			continueLabel={submitting ? 'Saving...' : 'Accept & Continue'}>
			<div
				onScroll={handleScroll}
				className="ob-terms-scroll"
				style={{
					height: 280,
					overflowY: 'auto',
					background: '#13161f',
					border: '1px solid #262b3a',
					borderRadius: 10,
					padding: 16,
					color: '#cfd3de',
					fontSize: 13,
					lineHeight: 1.6,
				}}>
				<p className="font-semibold mb-2">Terms of Service</p>
				<p className="mb-3 text-[#8a90a2]">Last Updated: May 2, 2025</p>

				<p className="font-semibold mt-3">1. Introduction</p>
				<p>
					Welcome to ValueBeaver Inc., operating as WagerWiz ("we", "our", or "us"). These Terms of Service ("Terms") govern your use of our website, mobile application, and any
					tools, data, or services provided (collectively, the "Service"). Our Privacy Policy, available on our website, also governs your use of our Service and explains how we
					collect, safeguard, and disclose information that results from your use of our Service. Your use of the Services is also subject to all applicable laws and regulations.
				</p>
				<p className="mt-2">
					By accessing or using the Service and/or creating a WagerWiz account, now or at any point in the future, you acknowledge that you have read and understood these Terms and
					agree to be bound by them. If you do not agree to the Terms, you are not permitted to use the Service and as applicable, uninstall the application and close the website
					page.
				</p>

				<p className="font-semibold mt-3">2. Communications</p>
				<p>
					By creating an account, you agree to receive newsletters, promotional materials, and other communications from us. You may opt out at any time by following the
					unsubscribe link or contacting support (support@thewagerwiz.com).
				</p>

				<p className="font-semibold mt-3">3. Purchases & Subscriptions</p>
				<p>
					Some features of the Service require a subscription ("Subscription(s)"). By purchasing a Subscription, you agree to provide accurate billing information and authorize us
					to charge your selected payment method on a recurring basis. Subscriptions automatically renew unless canceled before the next billing cycle. Subscription fees are
					non-refundable except where required by law.
				</p>
				<p className="mt-2">
					You may cancel at any time through the Site before the end of the next billing period. We will not refund or credit any partially used billing periods. When you cancel a
					WagerWiz subscription, you will continue to have access to that subscription for the remainder of your applicable billing cycle, after which the subscription will
					automatically end.
				</p>

				<p className="font-semibold mt-3">4. Free Trials</p>
				<p>
					We may offer a Free Trial at our discretion. Unless you cancel the subscription prior to the end of the free trial period, we will charge you the subscription fee for
					your next billing cycle at the end of the applicable free trial. We reserve the right to cancel or suspend part or all of our Services or the Site at any time at our sole
					discretion. Abuse of Free Trial offers, such as multiple sign-ups, is strictly prohibited.
				</p>

				<p className="font-semibold mt-3">5. Fee Changes</p>
				<p>We may modify Subscription fees with reasonable prior notice. If you continue using the Service after a fee change, you agree to the new rates.</p>

				<p className="font-semibold mt-3">6. Refund Policy</p>
				<p>Except as required by law, all payments are final and non-refundable.</p>

				<p className="font-semibold mt-3">7. Prohibited Uses</p>
				<ul className="list-disc list-inside space-y-1 mt-1">
					<li>For any unlawful activity, including fraud or money laundering.</li>
					<li>As a sportsbook employee, trader, or in any role at a daily fantasy pick'em site.</li>
					<li>To attempt to manipulate or disrupt betting markets.</li>
					<li>To scrape, copy, or redistribute Service data without permission.</li>
					<li>To use multiple accounts to exploit promotions, trials, or pricing structures.</li>
					<li>To reverse engineer, decompile, or disassemble any technology, software or content.</li>
					<li>To upload or transmit content you do not have a right to transmit.</li>
					<li>To upload or transmit content that infringes any IP rights.</li>
				</ul>
				<p className="mt-2">
					Except as expressly authorized by us in writing, you agree not to reproduce, republish, upload, post, transmit, distribute, copy, publicly display or otherwise use any
					content or derivative works based on the website. You agree to indemnify, defend and hold harmless us and our affiliates from any claims arising out of your violation of
					these Terms.
				</p>

				<p className="font-semibold mt-3">8. No Gambling Advice</p>
				<p>
					You are solely responsible for your betting decisions. WagerWiz provides analytical tools and information relating to sports related outcomes. Any and all advice and
					information is provided with no guarantee of profit or outcome and is for entertainment purposes only.
				</p>

				<p className="font-semibold mt-3">9. Intellectual Property</p>
				<p>
					All content, features, underlying software, and functionality of the Service are owned by ValueBeaver Inc. and protected by intellectual property laws. You may not
					reproduce, distribute, or use our proprietary content without prior written consent.
				</p>

				<p className="font-semibold mt-3">10. Disclaimers & Limitation of Liability</p>
				<p>
					The Service is provided "as is" and "as available" without warranties. No guarantees are made regarding accuracy or performance. Use of any material is at your own risk.
				</p>

				<p className="font-semibold mt-3">11. Governing Law</p>
				<p>These Terms are governed by the laws of Ontario, Canada. Disputes will be resolved through binding arbitration in Ontario, and users waive class-action rights.</p>

				<p className="font-semibold mt-3">12. Amendments to Terms</p>
				<p>We may update these Terms at any time. Continued use of the Service after updates means you accept the revised Terms.</p>

				<p className="font-semibold mt-3">13. Contact Us</p>
				<p className="mb-2">For any questions regarding these Terms, please contact us at support@thewagerwiz.com.</p>
			</div>

			<label
				className={`mt-5 flex items-start gap-3 select-none ${hasScrolled ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
				onClick={() => hasScrolled && setAgreed(!agreed)}>
				<span className={`ob-checkbox ${agreed ? 'ob-checkbox-checked' : ''} ${!hasScrolled ? 'ob-checkbox-disabled' : ''}`}>
					{agreed ? <span style={{ fontSize: 12, lineHeight: 1 }}>&#10003;</span> : null}
				</span>
				<span className="text-sm text-[#cfd3de] leading-snug">
					I've read and agree to the WagerWiz Terms of Service.
					{!hasScrolled && <span className="block text-[#8a90a2] text-xs mt-1">Scroll to the bottom of the terms above to enable.</span>}
				</span>
			</label>
		</OnboardingShell>
	);
};
